<template>
    <div class="rich-text-editor">
        <textarea
            id="editor1"
            name="editor1"
            cols="30"
            rows="10"
        />
    </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex';

import { tailwindConfig } from '@/data/tailwind';
import { drawerHelperMixin, TYPE_IMAGE } from '@/mixins/drawer-helper';
import { SHOW_DRAWER } from '@/store/types/mutation-types';

export default {
    mixins: [drawerHelperMixin],

    props: {
        input: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            richText: '',

            addMediaState: '',
            addLinkState: '',
            addMediaStateBoolean: false,
            mediaIdRegex: /([\w]+).ashx/,

            fontFamily: '',
            fontSize: '',
            ckeditorColorsList: '',
            colorsClassValue: {},
        };
    },

    computed: {
        ...mapState({
            selectedContent: state => state.page.selectedContent,
            selectedElements: state => state.page.selectedElements,
            selectedMedia: state => state.page.selectedMedia,
        }),
    },

    watch: {
        input(val) {
            window.CKEDITOR.instances.editor1.setData(val);
            this.richText = val;
        },

        selectedContent() {
            this.addLinkState = true;
        },

        selectedMedia(val) {
            if (val && this.selectedIsRichText) {
                this.addMediaStateBoolean = true;
            }
        },
    },

    beforeMount() {
        this.setMenuValues();
    },

    mounted() {
        this.init();
    },

    methods: {
        ...mapMutations({
            showDrawer: SHOW_DRAWER,
        }),

        init() {
            const vm = this;

            // Initialize CKEditor and pass it's config object
            const editor = window.CKEDITOR.replace('editor1', {
                plugins: 'a11yhelp,basicstyles,colorbutton,contextmenu,enterkey,entities,filebrowser,floatingspace,font,format,horizontalrule,htmlwriter,link,list,magicline,removeformat,scayt,showborders,specialchar,tab,toolbar,undo,uploadimage,wsc,wysiwygarea,dialog,dialogui,sourcedialog,dialogadvtab',
                extraPlugins: 'templates',
                removePlugins: 'elementspath',
                removeButtons: 'Anchor,Copy,Cut,Indent,Outdent,Paste',
                toolbarGroups: [
                    { name: 'styles', groups: ['styles'] },
                    { name: 'basicstyles', groups: ['basicstyles'] },
                    { name: 'paragraph', groups: ['list'] },
                    { name: 'editing', groups: ['spellchecker'] },
                    { name: 'links', groups: ['links'] },
                    { name: 'insert', groups: ['insert'] },
                    { name: 'colors', groups: ['colors'] },
                    { name: 'basicstyles', groups: ['cleanup'] },
                ],
                contentsCss: [
                    `${ process.env.VUE_APP_CM_URL }/-/media/foundation/theming/static/appcss`,
                    `${ process.env.VUE_APP_CM_URL }/-/media/foundation/theming/static/vendorcss`,
                ],
                autoGrow_onStartup: true,
                resize_enabled: false,
                // allowedContent property disables the content filtering which allows any markup to be passed through and saved as is
                allowedContent: true,
                entities: false,
                fontSize_sizes: this.fontSize,
                fontSize_style: {
                    element: 'span',
                    attributes: { class: 'f-text-#(size)' },
                    overrides: [{ element: 'span', attributes: { size: null } }],
                },
                font_names: this.fontFamily,
                font_style: {
                    element: 'span',
                    attributes: { class: 'f-font-#(family)' },
                    overrides: [{ element: 'font', attributes: { face: null } }],
                },
                colorButton_colors: this.ckeditorColorsList,
                colorButton_foreStyle: {
                    element: 'span',
                    attributes: { class: 'f-text-#(color)' },
                },
                colorButton_backStyle: {
                    element: 'span',
                    attributes: { class: 'f-bg-#(color)' },
                },
                colorButton_colorsPerRow: 10,
                on: {
                    change(evt) {
                        const editorHtmlString = evt.editor.getData();
                        // CKEditor will add hex values as classes so on each update of the wysiwyg, search
                        // for a hex value in the matching class string and replace it with a color class if it exists
                        const hexAsClassMatch = /-#([a-f0-9]{6})/gi;
                        const newHtmlString = editorHtmlString.replace(hexAsClassMatch, (match, hexValue) => {
                            for (const [key, value] of Object.entries(vm.colorsClassValue)) {
                                if (hexValue === value) {
                                    return `-${ key }`;
                                }
                            }
                        });

                        // Update the richText property on changes
                        if (newHtmlString && newHtmlString !== editorHtmlString) {
                            evt.editor.setData(newHtmlString, { internal: true });
                            vm.richText = newHtmlString;
                            const iframearea = document.querySelector('.cke_wysiwyg_frame');
                            iframearea.contentWindow.document.body.innerHTML = newHtmlString;
                        } else {
                            vm.richText = editorHtmlString;
                        }
                    },
                    // Temporarily disables the focus trap for flex modals when CKEditor is open
                    dialogShow() {
                        vm.$root.$emit('toggle-focus-back-in', false);
                    },
                    dialogHide() {
                        vm.$root.$emit('toggle-focus-back-in', true);
                    },
                },
            });

            // Add insert image command to construct markup to display images
            editor.addCommand('insertimage', {
                exec(edt) {
                    let imageId = '';

                    if (edt.getSelectedHtml().$.firstElementChild && edt.getSelectedHtml().$.firstElementChild.tagName === 'IMG') {
                        const mediaIdMatch = edt.getSelectedHtml().$.firstElementChild.src.match(vm.mediaIdRegex);
                        if (mediaIdMatch.length === 2) {
                            imageId = mediaIdMatch[1].replace(/([0-z]{8})([0-z]{4})([0-z]{4})([0-z]{4})([0-z]{12})/, '$1-$2-$3-$4-$5');
                        }
                    }

                    if (imageId) {
                        vm.addMediaState = 'replaceEmbed';
                        vm.openMediaDrawer(TYPE_IMAGE, imageId);
                    } else {
                        vm.addMediaState = 'embed';
                        vm.openMediaDrawer(TYPE_IMAGE);
                    }

                    vm.$root.$on('flex::drawer::hidden', () => {
                        if (vm.selectedMedia && vm.selectedIsRichText && vm.addMediaStateBoolean) {
                            const mediaUrl = `${ process.env.VUE_APP_CM_URL }/-/media/${ vm.selectedMedia.id.replace(/-/gi, '').toLowerCase() }.ashx`;
                            // eslint-disable-next-line
                        const mediaElement = new window.CKEDITOR.htmlWriter();
                            mediaElement.openTag('img');
                            mediaElement.attribute('src', mediaUrl);
                            if (vm.selectedMedia.altText) mediaElement.attribute('alt', vm.selectedMedia.altText);
                            if (vm.selectedMedia.width) mediaElement.attribute('width', vm.selectedMedia.width);
                            if (vm.selectedMedia.height) mediaElement.attribute('height', vm.selectedMedia.height);
                            mediaElement.openTagClose('img', true);
                            edt.insertHtml(mediaElement.getHtml());
                            vm.addMediaState = '';
                            vm.addMediaStateBoolean = false;
                            vm.$root.$off('flex::drawer::hidden');
                        }
                    });
                },
            });

            // Add image button and link to image commmand
            editor.ui.addButton('Image', {
                label: 'Insert Image',
                command: 'insertimage',
                toolbar: 'insert,0',
                icon: 'Image',
            });

            // Add internal link command to construct internal link markup
            editor.addCommand('internallink', {
                exec(edt) {
                    vm.showDrawer('navigation-tree-content-selector');

                    vm.$root.$on('flex::drawer::hidden', () => {
                        if (vm.selectedContent.id && vm.selectedIsRichText && vm.addLinkState) {
                            const internalLinkURL = `${ process.env.VUE_APP_CM_URL }/link.aspx?_id=${ vm.selectedContent.id.replace(/-/gi, '').toUpperCase() }&amp;_z=z`;
                            // eslint-disable-next-line
                        let linkElement = new window.CKEDITOR.htmlWriter();
                            linkElement.openTag('a');
                            linkElement.attribute('href', internalLinkURL);
                            linkElement.attribute('target', '_blank');
                            linkElement.attribute('rel', 'noopener noreferrer');
                            linkElement.openTagClose('a', false);
                            linkElement.text(edt.getSelection().getSelectedText());
                            linkElement.closeTag('a');
                            edt.insertHtml(linkElement.getHtml(), 'text');
                        }
                        vm.addLinkState = false;
                        vm.$root.$off('flex::drawer::hidden');
                    });
                },
            });

            // Add internal link button and link it to internal link command
            editor.ui.addButton('InternalLink', {
                label: 'Internal Link',
                command: 'internallink',
                toolbar: 'links',
                icon: 'Templates',
            });

            // Add media link command to construct media link markup
            editor.addCommand('medialink', {
                exec(edt) {
                    vm.openMediaDrawer();

                    vm.$root.$on('flex::drawer::hidden', () => {
                        if (vm.selectedMedia && vm.selectedIsRichText && vm.addMediaStateBoolean) {
                            const mediaUrl = `${ process.env.VUE_APP_CM_URL }/-/media/${ vm.selectedMedia.id.replace(/-/gi, '').toLowerCase() }.ashx`;
                            // eslint-disable-next-line
                        let linkElement = new window.CKEDITOR.htmlWriter();
                            linkElement.openTag('a');
                            linkElement.attribute('href', mediaUrl);
                            linkElement.attribute('target', '_blank');
                            linkElement.attribute('rel', 'noopener noreferrer');
                            linkElement.openTagClose('a', false);
                            linkElement.text(edt.getSelection().getSelectedText());
                            linkElement.closeTag('a');
                            edt.insertHtml(linkElement.getHtml(), 'text');
                        }
                        vm.addMediaStateBoolean = false;
                        vm.$root.$off('flex::drawer::hidden');
                    });
                },
            });

            // Add media link button and link it to media link command
            editor.ui.addButton('MediaLink', {
                label: 'Media Link',
                command: 'medialink',
                toolbar: 'links',
                icon: 'easyimageside',
            });

            window.CKEDITOR.domReady(() => {
                const containerElem = document.querySelector('.rich-text-editor');
                const toolbarElem = document.querySelector('.cke_top');

                if (!containerElem || !toolbarElem) return;

                containerElem.style.setProperty('--rte-toolbar-height', `${ toolbarElem.offsetHeight }px`);
            });
        },

        onSaveOrLeaveEditor() {
            this.$emit('output', this.richText);
        },

        setMenuValues() {
            // Create CKEditor config string for font families
            const fontFamilies = Object.entries(tailwindConfig.theme.fontFamily);
            fontFamilies.forEach((el) => {
                this.fontFamily += `${ el[1] }/${ el[0] };`;
            });

            // Create CKEditor config string for font sizes
            const fontSizes = Object.entries(tailwindConfig.theme.fontSize)
                .sort((a, b) => Number(a[0]) - Number(b[0])); // required because it was sorting by string value
            fontSizes.forEach((el) => {
                this.fontSize += `${ el[1] }/${ el[0] };`;
            });


            const colorsList = Object.entries(tailwindConfig.theme.colors).flatMap((entry) => {
                const [color, val] = entry;
                if (typeof val === 'object' && val !== null) {
                    return Object.entries(val).map((cEntry) => {
                        const [varNum, cVal] = cEntry;
                        return [`${ color }-${ varNum }`, cVal];
                    });
                }
                return [[color, val]];
            });

            // Create CKEditor config string for color options and css class to hex value object map
            colorsList.forEach((el) => {
                if (el[0] === 'transparent') {
                    this.ckeditorColorsList += '';
                } else if (typeof el[1] === 'object') {
                    Object.keys(el[1]).forEach((key) => {
                        this.ckeditorColorsList += `${ el[0] }-${ key }/${ el[1].substr(1) },`;
                        this.colorsClassValue[`${ el[0] }-${ key }`] = `${ el[1].substr(1) }`;
                    });
                } else {
                    this.ckeditorColorsList += `${ el[0] }/${ el[1].substr(1) },`;
                    this.colorsClassValue[`${ el[0] }`] = `${ el[1].substr(1) }`;
                }
            });
        },

        selectedIsRichText() {
            return Object.prototype.hasOwnProperty.call(this.selectedElements[0], 'richText');
        },
    },
};
</script>

<style lang="scss">
.rich-text-editor {
    --rte-toolbar-height: 42px;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    background-color: white;
    color: rgba(black, 0.8);
    padding: 2rem;

    .cke_editor_editor1 {
        height: 100%;
        overflow-y: hidden;

        .cke_inner {
            height: 100%;

            .cke_contents {
                height: calc(100% - var(--rte-toolbar-height)) !important;
            }
        }
    }
}
</style>
